import type { ReceiptData } from '@matix/types';

/**
 * The named automatic promotions on a receipt, and the unnamed rest of its
 * discount — ONE rule for every layout that prints them.
 *
 * The header carries a single discount figure and each promotion is a named
 * PART of it, not a further deduction. So every layout prints the named parts
 * as their own rows and lets its generic "Discount" row carry only what is left
 * unnamed (the cashier's hand discount, a coupon, redeemed points). Together
 * they equal `totals.discount` exactly and every number on the paper counts
 * once. The built-in slip has printed it this way since promotions shipped;
 * the Receipt Designer, the A4 sheet and the A4 designer now follow it too.
 */

type Promotion = NonNullable<ReceiptData['promotions']>[number];

/** The promotion's name in the slip's language, falling back to the other. */
export function promotionLabel(p: Pick<Promotion, 'name' | 'nameAr'>, arabic: boolean): string {
  const ar = (p.nameAr ?? '').trim();
  const en = (p.name ?? '').trim();
  return arabic ? ar || en : en || ar;
}

/**
 * The promotions whose money is inside `totals.discount` — every one except an
 * item-level promotion booked on its lines (level 'line', 2026-10-07+), which
 * the receipt already shows in those lines' discount. Every layout that names
 * promotions reads this list, never `data.promotions` directly.
 */
export function headerPromotions(data: Pick<ReceiptData, 'promotions'>): Promotion[] {
  return (data.promotions ?? []).filter((p) => p.level !== 'line');
}

export interface NamedDiscount {
  /** Promotions that took money off, in the order the server applied them. */
  promotions: Array<{ label: string; amount: number }>;
  /** `totals.discount` less the named part. 0 when the promotions explain it all. */
  unnamed: number;
}

export function splitNamedDiscount(data: ReceiptData, arabic: boolean): NamedDiscount {
  // An item-level promotion booked on its lines (level 'line') is already in
  // those lines' discount and outside totals.discount — naming it here would
  // print the same money twice.
  const promotions = headerPromotions(data)
    .filter((p) => p.amount > 0)
    .map((p) => ({ label: promotionLabel(p, arabic), amount: p.amount }));
  const named = promotions.reduce((sum, p) => sum + p.amount, 0);
  const rest = Math.max(0, data.totals.discount ?? 0) - named;
  // Half a cent of slack: two independently-rounded server figures, and a
  // residual of 0.001 must read as "fully named", not print a row.
  return { promotions, unnamed: rest > 0.005 ? rest : 0 };
}
